import { useEffect, useRef } from "react";

interface UseSearchOnEnterProps {
  isEnabled: boolean;
  onSearch: () => void;
  searchParams: any;
}

export const useSearchOnEnter = ({
  isEnabled,
  onSearch,
  searchParams,
}: UseSearchOnEnterProps) => {
  const onSearchRef = useRef(onSearch);
  // Initialize with current searchParams to prevent initial duplicate search
  const lastSearchParamsRef = useRef<any>(JSON.parse(JSON.stringify(searchParams)));

  // Keep onSearch reference updated without triggering re-renders
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore non-Enter keys or when search is disabled
      if (e.key !== "Enter" || !isEnabled) {
        return;
      }

      // Compare current params with last executed search params
      const currentParams = JSON.stringify(searchParams);
      const lastParams = JSON.stringify(lastSearchParamsRef.current);
      const paramsChanged = currentParams !== lastParams;

      // Only trigger search if parameters have changed since last search
      if (paramsChanged) {
        e.preventDefault();
        onSearchRef.current();
        // Update ref after successful search to prevent duplicate searches
        lastSearchParamsRef.current = JSON.parse(currentParams);
      } else {
        // Warn user that search parameters haven't changed
        console.warn("Search parameters have not changed. Press the Search button or modify parameters to search again.");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isEnabled, searchParams]);
};